import { format } from 'date-fns';
import type {
  CarrierSchedule,
  CarrierScheduleWorkflowData,
  CarrierScheduleDeleteWorkflowData,
  Schedule,
  ScheduleEntry,
} from '../../assets/types';

export const deleteCarrierScheduleFromWorkflowData = (
  assetPayload: CarrierScheduleDeleteWorkflowData,
  schedule: Schedule,
) => {
  const { serviceMethods, carrier, scheduleEntryFrom, deleteCarriersAndMethods } = assetPayload;
  const entriesToUpdate = schedule.entries.filter((val) => scheduleEntryFrom.includes(val.from));
  const unAffectedEntries = schedule.entries.filter((val) => !scheduleEntryFrom.includes(val.from));
  const newEntries = !serviceMethods?.length
    ? entriesToUpdate.map((e) => {
        return {
          ...e,
          carrierSchedules: e.carrierSchedules?.filter(
            (cs) =>
              cs.carrierId !== carrier ||
              (cs.carrierId === carrier && !!cs?.carrierServiceMethodIds?.length && !deleteCarriersAndMethods),
          ),
        };
      })
    : entriesToUpdate.map((e) => ({
        ...e,
        carrierSchedules: e.carrierSchedules?.map((cs) => ({
          ...cs,
          carrierServiceMethodIds:
            cs.carrierServiceMethodIds?.filter((mtd: any) => !serviceMethods.includes(mtd)) ?? [],
        })),
      }));
  return {
    ...schedule,
    entries: [...unAffectedEntries, ...newEntries],
  };
};
export const addCarrierScheduleFromWorkflowData = (assetPayload: CarrierScheduleWorkflowData, schedule: Schedule) => {
  const { serviceMethods, cutoffExceptions, carrier, cutoffTimes, scheduleEntryFrom } = assetPayload;
  const createdCarrierSchedule = {
    carrierId: carrier,
    cutoffExceptions:
      cutoffExceptions?.map((e: any) => {
        return {
          date: format(e.date, 'yyyy-MM-dd'),
          cutoff: e.cutoffTime ? format(e.cutoffTime, 'HH:mm') : undefined,
        };
      }) ?? [],
    cutoffTimes,
    carrierServiceMethodIds: serviceMethods ?? [],
  };
  const entriesToUpdate = schedule.entries.filter((val) => scheduleEntryFrom.includes(val.from));
  const unAffectedEntries = schedule.entries.filter((val) => !scheduleEntryFrom.includes(val.from));
  if (!assetPayload?.serviceMethods?.length) {
    // simple carrier level case
    const newEntries = entriesToUpdate.map((val: any) => {
      return {
        ...val,
        carrierSchedules: [
          ...val.carrierSchedules.filter((s: any) => {
            return s.carrierId !== carrier || !!s?.carrierServiceMethodIds?.length;
          }),
          createdCarrierSchedule,
        ],
      };
    });
    return {
      ...schedule,
      entries: [...unAffectedEntries, ...newEntries],
    };
  } else {
    // more complicated service method level case
    return {
      ...schedule,
      entries: [
        ...unAffectedEntries,
        ...entriesToUpdate.map((e) => {
          return modifyEntryForUpdate(e, createdCarrierSchedule);
        }),
      ],
    };
  }
};

const modifyEntryForUpdate = (scheduleEntry: ScheduleEntry, createdCarrierScehdule: CarrierSchedule) => {
  const affectedSchedules =
    scheduleEntry?.carrierSchedules?.filter((val) => {
      if (
        val?.carrierServiceMethodIds?.length > 0 &&
        createdCarrierScehdule?.carrierId === val?.carrierId &&
        // determine if there is a set intersection
        // if the set of elements is different size than
        // the two list added together, we have intersections
        new Set([...(createdCarrierScehdule.carrierServiceMethodIds ?? []), ...(val?.carrierServiceMethodIds ?? [])])
          .size <
          (createdCarrierScehdule?.carrierServiceMethodIds?.length ?? 0) + (val?.carrierServiceMethodIds?.length ?? 0)
      ) {
        return true;
      }
      return false;
    }) ?? [];
  // schedules that apply to all methods,
  // apply to a different carrier,
  // or apply to all methods are not included
  const unaffectedSchedules =
    scheduleEntry?.carrierSchedules?.filter((val) => {
      if (
        val?.carrierServiceMethodIds?.length < 1 ||
        createdCarrierScehdule?.carrierId !== val?.carrierId ||
        new Set([...(createdCarrierScehdule.carrierServiceMethodIds ?? []), ...(val?.carrierServiceMethodIds ?? [])])
          .size ===
          (createdCarrierScehdule?.carrierServiceMethodIds?.length ?? 0) + (val?.carrierServiceMethodIds?.length ?? 0)
      ) {
        return true;
      }
      return false;
    }) ?? [];
  const modifiedCalendars = affectedSchedules
    ?.map((val) => {
      val.carrierServiceMethodIds = val.carrierServiceMethodIds.filter(
        (mtd) => !createdCarrierScehdule.carrierServiceMethodIds?.includes(mtd),
      );
      return val;
    })
    .filter((val) => {
      // if all the service methods have been updated
      // we don't want to say the old entry applies to all methods
      // (which is how an empty array is treated)
      // so we remove it
      return val.carrierServiceMethodIds.length;
    });
  modifiedCalendars?.push(createdCarrierScehdule);
  return { ...scheduleEntry, carrierSchedules: [...(unaffectedSchedules ?? []), ...(modifiedCalendars ?? [])] };
};
